<div fxLayout="column" fxLayoutGap="12px" class="cipo--height--full">
    <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="12px" class="p-x-md cipo--overflow--hidden">
        <mat-icon class="cipo-text--primary-900" aria-hidden="true">dashboard_customize</mat-icon>
        <h3
            class="m-a-0 cipo--text--ellipsed"
            data-testid="module-config-screen-designer-title"
            [title]="screen().name">
            {{ screen().name }}
        </h3>
        @if (screen().anyFieldInUse) {
            <span
                class="mat-body-2 cipo-bg--warn-400 cipo-text--black-800 p-x-sm cipo--border-radius--xs"
                data-testid="module-config-screen-designer-in-use">
                {{ "modules.screenDesigner.screenInUse" | translate }}
            </span>
        }
    </div>

    <div fxLayout="row" fxLayoutAlign="space-between end" fxLayoutGap="16px">
        @if (canUpdate()) {
            <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="8px" fxFlex="none">
                <button
                    mat-stroked-button
                    data-testid="module-config-screen-designer-action-add-field"
                    [matMenuTriggerFor]="addFieldMenu"
                    [disabled]="isBusy()">
                    <mat-icon aria-hidden="true">add</mat-icon>
                    {{ "modules.screenDesigner.addField" | translate }}
                    <mat-icon iconPositionEnd aria-hidden="true">arrow_drop_down</mat-icon>
                </button>
                <button
                    mat-stroked-button
                    data-testid="module-config-screen-designer-action-add-annotation"
                    [disabled]="isBusy()"
                    (click)="addAnnotation()">
                    <mat-icon aria-hidden="true">note_add</mat-icon>
                    {{ "modules.screenDesigner.addAnnotation" | translate }}
                </button>
            </div>
        }
        <span class="mat-body-2 cipo-text--black-500">{{ "modules.screenDesigner.hint" | translate }}</span>
    </div>

    @if (loading() && !items().length) {
        <cipo-module-config-designer-skeleton fxFlex label="a11y.moduleConfig.loadingScreenFields" />
    } @else if (failed()) {
        <cipo-load-error
            message="modules.screenDesigner.loadError"
            testId="module-config-screen-designer-retry"
            (retry)="load()" />
    } @else {
        <div fxFlex fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="16px" class="cipo--min-height--400">
            <div
                fxFlex
                fxLayout="column"
                class="cipo--min-height--300 cipo--border-radius--xs cipo--overflow--hidden cipo--border--lite cipo-bg--black-50"
                data-testid="module-config-screen-designer-grid">
                <gridster fxFlex [options]="gridOptions()">
                    @for (item of items(); track item.uid) {
                        <gridster-item
                            class="designer-item"
                            data-testid="module-config-screen-designer-item"
                            [item]="item"
                            [style.background-color]="fieldCardBackground(item.field)"
                            [class.designer-item--annotation]="isAnnotation(item)"
                            [class.designer-item--unsaved]="isItemUnsaved(item)"
                            [class.designer-item--error]="item.unknownType">
                            <div
                                class="cipo--height--full p-x-sm"
                                fxLayout="row"
                                fxLayoutAlign="start center"
                                fxLayoutGap="8px">
                                @if (isAnnotation(item)) {
                                    <mat-icon class="designer-item__icon" aria-hidden="true">edit_note</mat-icon>
                                    <div
                                        fxFlex
                                        class="designer-item__annotation mat-body-2"
                                        [innerHTML]="item.field.annotation"></div>
                                } @else {
                                    <div class="designer-item__metadata cipo--gap--sm">
                                        @if (!item.unknownType) {
                                            <cipo-field-type-indicator
                                                class="designer-item__type"
                                                [field]="itemTypeFields().get(item.uid)"
                                                size="md"
                                                orientation="vertical" />
                                        }
                                        <div class="cipo--flex--column cipo--flex--1 cipo--overflow--hidden">
                                            <span
                                                class="cipo--text-weight--medium cipo--text--ellipsed"
                                                [title]="item.field.name">
                                                {{ item.field.name }}
                                            </span>
                                            @if (item.unknownType) {
                                                <span class="mat-caption cipo-text--warn-900">
                                                    {{ "modules.screenDesigner.unknownType" | translate }}
                                                </span>
                                            } @else {
                                                <span
                                                    class="cipo--text-size--sm cipo--text-height--md cipo-text--black-500 cipo--text--ellipsed"
                                                    [title]="item.field.label">
                                                    {{
                                                        "modules.screenDesigner.labelValue"
                                                            | translate: { label: item.field.label || "-" }
                                                    }}
                                                </span>
                                            }
                                        </div>
                                    </div>
                                    @if (item.field.isRequired) {
                                        <mat-icon
                                            class="cipo-text--black-500"
                                            aria-hidden="false"
                                            [attr.aria-label]="'modules.screenDesigner.requiredField' | translate"
                                            [matTooltip]="'modules.screenDesigner.requiredField' | translate">
                                            lock
                                        </mat-icon>
                                    }
                                }

                                @if (canUpdate()) {
                                    <div
                                        class="gridster-item-content designer-item__actions"
                                        fxLayout="row"
                                        fxLayoutAlign="end center"
                                        fxFlex="none">
                                        @if (
                                            isAnnotation(item) ||
                                            (!isItemUnsaved(item) &&
                                                !item.unknownType &&
                                                !item.field.isContractSystemField)
                                        ) {
                                            <button
                                                mat-icon-button
                                                data-testid="module-config-screen-designer-item-edit"
                                                [disabled]="isBusy()"
                                                [title]="'actions.edit' | translate"
                                                (click)="editItem(item)">
                                                <mat-icon aria-hidden="true">edit</mat-icon>
                                            </button>
                                        }
                                        @if (!item.field.isRequired) {
                                            <button
                                                mat-icon-button
                                                data-testid="module-config-screen-designer-item-remove"
                                                [disabled]="isBusy() || (!isAnnotation(item) && !item.field.canRemove)"
                                                [title]="
                                                    (isAnnotation(item) || item.field.canRemove
                                                        ? 'modules.screenDesigner.removeFromForm'
                                                        : 'modules.screenDesigner.usedInConditions'
                                                    ) | translate
                                                "
                                                (click)="removeItem(item)">
                                                <mat-icon aria-hidden="true">remove_circle_outline</mat-icon>
                                            </button>
                                        }
                                    </div>
                                }
                            </div>
                        </gridster-item>
                    }
                </gridster>
            </div>

            <div
                fxFlex="350px"
                fxFlex.lt-md="100"
                fxLayout="column"
                fxLayoutGap="8px"
                class="cipo--min-height--300"
                data-testid="module-config-screen-designer-fields">
                <div fxLayout="column">
                    <div fxLayout="row" fxLayoutAlign="space-between center">
                        <h4 class="m-a-0">
                            {{ "modules.screenDesigner.fields" | translate }}
                            <span class="mat-caption cipo-text--black-500">({{ fields().length }})</span>
                        </h4>
                        @if (perContract()) {
                            <mat-checkbox
                                data-testid="module-config-screen-designer-contract-fields"
                                [checked]="showContractFields()"
                                [disabled]="isBusy()"
                                (change)="toggleContractFields($event.checked)">
                                {{ "modules.screenDesigner.showContractFields" | translate }}
                            </mat-checkbox>
                        }
                    </div>

                    <mat-form-field class="cipo-input cipo--width--full" subscriptSizing="dynamic">
                        <mat-label>{{ "general.search" | translate }}</mat-label>
                        <input
                            matInput
                            data-testid="module-config-screen-designer-fields-search"
                            [value]="fieldQuery()"
                            (input)="fieldQuery.set($any($event.target).value)" />
                        <mat-icon matSuffix aria-hidden="true">search</mat-icon>
                    </mat-form-field>
                </div>

                <div fxFlex fxLayout="column" class="cipo--gap--lg cipo--overflow-y--auto p-t-sm p-r-xs">
                    @if (loadingFields()) {
                        <cipo-module-config-list-skeleton
                            label="a11y.moduleConfig.loadingFields"
                            [lines]="2"
                            [actions]="2" />
                    } @else if (!visibleFields().length) {
                        <p class="mat-body-2 cipo-text--black-500 cipo--text--center m-t-md">
                            {{
                                (fields().length ? "notFound.nothingFound" : "modules.screenDesigner.noFields")
                                    | translate
                            }}
                        </p>
                    } @else {
                        @for (field of visibleFields(); track field.id) {
                            <cipo-field-card
                                data-testid="module-config-screen-designer-field"
                                [field]="field"
                                [changed]="isFieldUnsaved(field)"
                                [outlineLabel]="field.isContractSystemField ? field.label : field.name">
                                @if (canUpdate()) {
                                    <div class="cipo--flex--row cipo--align--center">
                                        @if (!isOnForm(field)) {
                                            <button
                                                mat-icon-button
                                                color="primary"
                                                data-testid="module-config-screen-designer-field-add"
                                                [disabled]="isBusy()"
                                                [title]="'modules.screenDesigner.addToForm' | translate"
                                                (click)="addToForm(field)">
                                                <mat-icon aria-hidden="true">add_circle_outline</mat-icon>
                                            </button>
                                        } @else if (!field.isRequired) {
                                            <button
                                                mat-icon-button
                                                data-testid="module-config-screen-designer-field-remove"
                                                [disabled]="isBusy() || !field.canRemove"
                                                [title]="
                                                    (field.canRemove
                                                        ? 'modules.screenDesigner.removeFromForm'
                                                        : 'modules.screenDesigner.usedInConditions'
                                                    ) | translate
                                                "
                                                (click)="removeFromForm(field)">
                                                <mat-icon aria-hidden="true">remove_circle_outline</mat-icon>
                                            </button>
                                        }
                                        <button
                                            mat-icon-button
                                            data-testid="module-config-screen-designer-field-edit"
                                            [disabled]="
                                                isBusy() || field.isContractSystemField || isFieldUnsaved(field)
                                            "
                                            [title]="
                                                (isFieldUnsaved(field)
                                                    ? 'modules.screenDesigner.saveFormFirst'
                                                    : 'actions.edit'
                                                ) | translate
                                            "
                                            (click)="editField(field)">
                                            <mat-icon aria-hidden="true">edit</mat-icon>
                                        </button>
                                        @if (canDeleteField(field)) {
                                            <button
                                                mat-icon-button
                                                data-testid="module-config-screen-designer-field-delete"
                                                [disabled]="isBusy() || isFieldUnsaved(field) || isOnForm(field)"
                                                [title]="'actions.delete' | translate"
                                                (click)="deleteField(field)">
                                                <mat-icon aria-hidden="true">delete</mat-icon>
                                            </button>
                                        }
                                    </div>
                                }
                            </cipo-field-card>
                        }
                    }
                </div>
            </div>
        </div>
    }
</div>

<mat-menu #addFieldMenu="matMenu">
    @for (fieldType of addableTypes(); track fieldType.typeId) {
        @if (fieldType.displayTypes.length > 1) {
            <button
                mat-menu-item
                data-testid="module-config-screen-designer-add-field-type"
                [matMenuTriggerFor]="displayTypeMenu"
                [matMenuTriggerData]="{ fieldType: fieldType }">
                <mat-icon aria-hidden="true">{{ fieldType.icon }}</mat-icon>
                {{ fieldType.label | translate }}
            </button>
        } @else {
            <button
                mat-menu-item
                data-testid="module-config-screen-designer-add-field-type"
                (click)="createField(fieldType, fieldType.displayTypes[0])">
                <mat-icon aria-hidden="true">{{ fieldType.icon }}</mat-icon>
                {{ fieldType.label | translate }}
            </button>
        }
    }
</mat-menu>

<mat-menu #displayTypeMenu="matMenu">
    <ng-template matMenuContent let-fieldType="fieldType">
        @for (displayType of fieldType.displayTypes; track displayType.displayTypeId) {
            <button
                mat-menu-item
                data-testid="module-config-screen-designer-add-field-display"
                (click)="createField(fieldType, displayType)">
                <mat-icon aria-hidden="true">{{ displayType.icon }}</mat-icon>
                {{ displayType.label | translate }}
            </button>
        }
    </ng-template>
</mat-menu>
